import { useNotification } from 'admin';
import {
  getApiSamplesV1SamplesEvidenceListQueryKey,
  getApiSamplesV1SamplesListRetrieveQueryKey,
  getApiSamplesV1SamplesRetrieveQueryKey,
  getSampleVariantFromMaterial,
  isFieldErrorResponse,
  SampleType,
  type UpdateSampleBreakdownDTO,
  useApiSamplesV1SamplesEvidenceListSuspense,
  useApiSamplesV1SamplesRetrieveSuspense,
  useApiSamplesV1SamplesUpdate,
  useQueryClient,
} from 'api';
import { ActionBar, SampleFormFields, TotalWeightWarning, useManageEvidence, useSiteMaterial } from 'features';
import { Form, useForm, useWatch } from 'form';
import { Suspense } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ActionRow,
  Box,
  Button,
  Grid,
  ListSkeletonLoader,
  Page,
  PhotoEvidenceMultiSelectList,
  Section,
  Stack,
  Typography,
  useBreakpoint,
} from 'ui';

import { SiteHeader } from '../../components/SiteHeader/SiteHeader';

type FormValues = {
  description: string;
  total_weight: number | null;
  breakdown: UpdateSampleBreakdownDTO[];
};

export const SampleEdit = () => {
  const { sampleId } = useParams();
  const { isMobile } = useBreakpoint();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { open } = useNotification();
  const { data: siteMaterial } = useSiteMaterial();
  const variant = getSampleVariantFromMaterial(siteMaterial.material_spec.title);
  const { data: sample } = useApiSamplesV1SamplesRetrieveSuspense(Number(sampleId));
  const { data: evidence } = useApiSamplesV1SamplesEvidenceListSuspense(Number(sampleId), { page_size: 10000 });
  const {
    selected,
    setSelected,
    deleteSelected,
    isPending: isDeletePending,
  } = useManageEvidence({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getApiSamplesV1SamplesEvidenceListQueryKey(Number(sampleId)),
      });
    },
  });
  const form = useForm<FormValues>({ defaultValues: sample });
  const { mutate, isPending: isUpdatePending } = useApiSamplesV1SamplesUpdate({
    mutation: {
      onError: (error) => {
        if (isFieldErrorResponse(error)) {
          Object.keys(error.response?.data).forEach((field) => {
            form.setError(field as keyof FormValues, { message: error.response.data[field] });
          });
        } else {
          open?.({
            message: `Failed to update sample: ${error}`,
            type: 'error',
          });
        }
      },
      onSuccess: () => {
        open?.({
          message: 'Successfully updated sample',
          type: 'success',
        });
        queryClient.invalidateQueries({
          queryKey: getApiSamplesV1SamplesListRetrieveQueryKey(siteMaterial.public_id),
        });
        queryClient.invalidateQueries({
          queryKey: getApiSamplesV1SamplesRetrieveQueryKey(sample.id),
        });
        navigate(-1);
      },
    },
  });
  const isPending = isDeletePending || isUpdatePending;

  const breakdownValues = useWatch({
    control: form.control,
    name: 'breakdown',
  });

  const totalWeight = useWatch({
    control: form.control,
    name: 'total_weight',
  });

  return (
    <Page>
      <Page.Title title="Edit Sample" />
      <Page.Divider />
      <Grid container>
        <Grid item xs={12} md={4}>
          <Section title="Site Details">
            <SiteHeader siteMaterial={siteMaterial} />
          </Section>
        </Grid>
        <Grid item xs={12} md={8}>
          <Section title="Sample">
            <Form
              {...form}
              onSubmit={(data) =>
                mutate({
                  data: {
                    ...sample,
                    breakdown: data.breakdown,
                    description: data.description,
                    total_weight: data.total_weight,
                  },
                  sampleId: sample.id,
                })
              }
            >
              <Stack direction={isMobile ? 'column' : 'row'} gap={3}>
                <Stack gap={2} sx={isMobile ? {} : { width: '50%' }}>
                  <Typography variant="body1" fontWeight="bold">
                    Sample
                  </Typography>
                  <TotalWeightWarning totalWeight={totalWeight} breakdownValues={breakdownValues || []} />
                  <Suspense fallback={<ListSkeletonLoader />}>
                    <SampleFormFields variant={variant} disableBreakdown={sample.type === SampleType.AG} />
                  </Suspense>
                </Stack>

                <Stack
                  gap={1}
                  sx={{
                    maxHeight: '50vh',
                  }}
                >
                  <Typography variant="body1" fontWeight="bold">
                    Sample Evidence
                  </Typography>
                  {selected.length > 0 && (
                    <Box
                      sx={{
                        position: 'sticky',
                        top: 0,
                      }}
                    >
                      <ActionBar
                        selected={selected.length}
                        handleClose={() => setSelected([])}
                        handleDelete={deleteSelected}
                        actionPending={isPending}
                      />
                    </Box>
                  )}
                  <PhotoEvidenceMultiSelectList
                    slotProps={{
                      imageList: {
                        cols: isMobile ? 3 : 5,
                      },
                    }}
                    evidencePhotos={evidence.results.map(({ id, file, preview }) => ({
                      id,
                      src: (preview || file) ?? '',
                    }))}
                    selected={selected}
                    onSelectionChange={setSelected}
                  />
                </Stack>
              </Stack>

              <ActionRow>
                <Button size="large" fullWidth variant="secondary" onClick={() => navigate(-1)}>
                  Cancel
                </Button>
                <Button size="large" fullWidth variant="primary" type="submit" loading={isPending}>
                  Submit
                </Button>
              </ActionRow>
            </Form>
          </Section>
        </Grid>
      </Grid>
    </Page>
  );
};
